import { create } from 'zustand';
import { Graphics } from 'pixi.js';

import type { WorldModel } from '../world/model';
import { tileIndex } from '../world/model';
import { tileToIso, TILE_WIDTH_PX, TILE_HEIGHT_PX, visibleTileRange, type CameraState } from './iso';
import type { SceneLayers } from './scene';

export type OverlayMode = 'none' | 'elevation' | 'biome' | 'material' | 'room';

interface DevOverlayState {
  mode: OverlayMode;
  setMode: (mode: OverlayMode) => void;
}

export const useDevOverlay = create<DevOverlayState>((set) => ({
  mode: 'none',
  setMode: (mode) => set({ mode }),
}));

export function overlayColor(
  mode: OverlayMode,
  material: number,
  biome: number,
  elevation: number,
  roomId: number,
): number | null {
  switch (mode) {
    case 'none':
      return null;
    case 'elevation': {
      const t = Math.min(255, Math.max(0, elevation));
      return (t << 16) | ((255 - t) << 8) | 0x40;
    }
    case 'biome': {
      const BIOME_COLORS = [
        0x7cfc00, 0x228b22, 0x87ceeb, 0xedc9af,
        0xb0c4de, 0x556b2f, 0x808080, 0x1e90ff,
      ];
      return BIOME_COLORS[biome % BIOME_COLORS.length]!;
    }
    case 'material': {
      const hue = (material * 137) % 360;
      return hslToRgb(hue, 0.7, 0.5);
    }
    case 'room': {
      if (roomId < 0) return 0x333333;
      const hue = (roomId * 67) % 360;
      return hslToRgb(hue, 0.8, 0.55);
    }
  }
}

function hslToRgb(h: number, s: number, l: number): number {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const ri = Math.round((r + m) * 255);
  const gi = Math.round((g + m) * 255);
  const bi = Math.round((b + m) * 255);
  return (ri << 16) | (gi << 8) | bi;
}

export interface DevOverlayRenderer {
  render: (model: WorldModel, cam: CameraState, viewportW: number, viewportH: number) => void;
  destroy: () => void;
}

export function createDevOverlayRenderer(layers: SceneLayers): DevOverlayRenderer {
  const g = new Graphics();
  layers.floorOverlayLayer.addChild(g);  // WORLD-SPACE, not overlayLayer

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ): void => {
    g.clear();
    const mode = useDevOverlay.getState().mode;
    if (mode === 'none') return;

    const r = visibleTileRange(cam, viewportW, viewportH, 1);
    const halfW = TILE_WIDTH_PX / 2;
    const halfH = TILE_HEIGHT_PX / 2;

    for (let ty = r.minY; ty <= r.maxY; ty++) {
      for (let tx = r.minX; tx <= r.maxX; tx++) {
        if (tx < 0 || tx >= model.width || ty < 0 || ty >= model.height) continue;
        const idx = tileIndex(tx, ty, model.width);
        const color = overlayColor(
          mode,
          model.tileMaterials[idx]!,
          model.tileBiomes[idx]!,
          model.tileElevations[idx]!,
          model.tileRoomIds[idx]!,
        );
        if (color === null) continue;

        const iso = tileToIso(tx, ty);
        g.poly([
          iso.x, iso.y - halfH,
          iso.x + halfW, iso.y,
          iso.x, iso.y + halfH,
          iso.x - halfW, iso.y,
        ]);
        g.fill({ color, alpha: 0.45 });
      }
    }
  };

  return {
    render,
    destroy: () => g.destroy(),
  };
}
